import { clone } from 'ramda';
import { AxiosError } from 'axios';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState, useReducer } from 'react';
import { NormalizedResponseData } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/entities/NormalizedResponseData';
import { wizardPreloadDataAdaptersMapping } from '../../utils';
import { ClientSideWizardContext, ClientSideWizardDispatchContext } from './ClientSideWizardContext';
import { defaultClientSideWizardStatesValues } from './defaultState';
import { ClientSideWizardReducer } from './ClientSideWizardReducer';
import {
  ClientSideWizardActionTypes,
  ClientSideWizardProviderProps,
  ClientSideWizardStateHandlerProps,
  ClientSideWizardStepConfiguration,
  PreloadedFormDataConfig,
  WizardNavigationActionsEnum,
} from './types';

const ClientSideWizardProvider: FC<PropsWithChildren<ClientSideWizardProviderProps>> = ({
  stepsConfig,
  processName,
  stateHandler,
  onProcessEnd,
  onCancel,
  onSubmit,
  onStepChange,
  preloadedFormDataConfig,
  onPreviousFirstStep,
  children,
}) => {
  const [state, dispatch] = useReducer(ClientSideWizardReducer, defaultClientSideWizardStatesValues);
  const [shouldNavigateTo, setShouldNavigateTo] = useState<Nullable<WizardNavigationActionsEnum>>(
    defaultClientSideWizardStatesValues.shouldNavigateTo,
  );
  const [networkCallErrors, setNetworkCallErrors] = useState<Nullable<NormalizedResponseData<any> | AxiosError>>(null);
  const [currentProcessState, setCurrentProcessState] = useState(state.currentProcessState);

  // Determine conditions for wizard state side effects
  const isCurrentProcessStateExists = useMemo(
    () => state.currentProcessState && state.currentProcessState.stepId,
    [state.currentProcessState],
  );
  const isFormDataExists = useMemo(() => state.formData && state.formData.data, [state.formData]);

  // Determine conditions for navigation callbacks
  const shouldGotoNextWizardStep = shouldNavigateTo === WizardNavigationActionsEnum.next;
  const shouldGotoPreviousWizardStep = shouldNavigateTo === WizardNavigationActionsEnum.prev;

  const stateHandlerDispatch = useCallback((obj: GenericObject) => {
    dispatch(action(ClientSideWizardActionTypes.SetClientSideWizardState, obj));
  }, []);

  // On-the-fly construction of form data state, based on step id
  const preloadedPristineFormConfig: PreloadedFormDataConfig = useMemo(() => ({ data: {}, currentStep: null }), []);
  preloadedPristineFormConfig.currentStep = preloadedFormDataConfig?.currentStep;
  Object.getOwnPropertyNames(stepsConfig).forEach(
    (stepKey: string) => ((preloadedPristineFormConfig as GenericObject).data[stepKey] = null),
  );

  // Pre-populate form steps with any preloaded data and initialize the wizard's state
  const preloadedFormState: PreloadedFormDataConfig = useMemo(
    () => (preloadedFormDataConfig ? preloadedFormDataConfig : preloadedPristineFormConfig),
    [preloadedFormDataConfig, preloadedPristineFormConfig],
  );

  // Pre-load any data to step config
  const formDataPreloader = useCallback(
    (currentStepConfig: ClientSideWizardStepConfiguration) => {
      if (!state.formData || !currentStepConfig.parameters.components) {
        return currentStepConfig;
      }

      return {
        ...currentStepConfig,
        parameters: {
          ...currentStepConfig.parameters,
          preloadedData: state.formData,
        },
      };
    },
    [state.formData],
  );

  const stepsStatus = useMemo(() => {
    const stepKeysThatHaveStepIndicator = Object.entries(stepsConfig).reduce((acc: string[], [key, value]) => {
      if (value.hasStepIndicator) {
        acc.push(key);
      }
      return acc;
    }, []);
    return {
      nextCompletedSteps: state.currentProcessState.stepId
        ? stepKeysThatHaveStepIndicator.indexOf(state.currentProcessState.stepId as string) + 1
        : 0,
      totalSteps: stepKeysThatHaveStepIndicator.length,
    };
  }, [stepsConfig, state.currentProcessState.stepId]);

  // Wizard navigation callbacks
  const goToNextWizardStep = useCallback(
    (formDataValues?: any) => {
      // Apply any custom process end logic else go to next step
      if (onProcessEnd && state.currentProcessState.stepId === 'END') {
        onProcessEnd(state.currentProcessState);
        dispatch(
          action(ClientSideWizardActionTypes.SetNetworkCallErrors, {
            ...state.errorState,
            errors: null,
          }),
        );
        return;
      }
      // initialization of next step's object
      if (isFormDataExists && isCurrentProcessStateExists && state.currentProcessState.stepId) {
        const stepsKeys = Object.keys(stepsConfig);
        const foundIndex = stepsKeys.indexOf(state.currentProcessState.stepId);
        if (formDataValues) {
          const newFormData = clone(state.formData);
          newFormData.data[state.currentProcessState.stepId].values = {
            ...newFormData.data[state.currentProcessState.stepId]?.values,
            ...formDataValues,
          };
          newFormData.previousStep = newFormData.currentStep;
          newFormData.currentStep = stepsKeys[foundIndex + 1];
          dispatch(
            action(ClientSideWizardActionTypes.SetClientSideWizardState, {
              formData: newFormData,
            }),
          );
        }
        dispatch(
          action(ClientSideWizardActionTypes.SetNetworkCallErrors, {
            ...state.errorState,
            errors: null,
          }),
        );
        setShouldNavigateTo(WizardNavigationActionsEnum.next);
      }
    },
    [
      stepsConfig,
      onProcessEnd,
      state.formData,
      state.errorState,
      isFormDataExists,
      state.currentProcessState,
      isCurrentProcessStateExists,
    ],
  );

  const goToPreviousWizardStep = useCallback(() => {
    if (state.currentProcessState.currentStepConfig?.isFirstStep) {
      onPreviousFirstStep && onPreviousFirstStep();
      setNetworkCallErrors(null);
    } else {
      setShouldNavigateTo(WizardNavigationActionsEnum.prev);
      setNetworkCallErrors(null);
    }
  }, [state.currentProcessState.currentStepConfig?.isFirstStep, onPreviousFirstStep]);

  // expose navigation methods to wizard state
  useEffect(() => {
    dispatch(
      action(ClientSideWizardActionTypes.SetNavigationAction, {
        goToNextWizardStep,
        goToPreviousWizardStep,
      }),
    );
  }, [goToNextWizardStep, goToPreviousWizardStep]);

  // expose errors to wizard state
  useEffect(() => {
    dispatch(
      action(ClientSideWizardActionTypes.SetNetworkCallErrors, {
        errors: networkCallErrors,
        setNetworkCallErrors,
      }),
    );
  }, [networkCallErrors]);

  // expose stepsConfig to wizard state
  useEffect(() => {
    dispatch(action(ClientSideWizardActionTypes.SetStepsConfig, stepsConfig));
  }, [stepsConfig]);

  useEffect(() => {
    // Initialize process state
    if (!isCurrentProcessStateExists) {
      const stepsKeys = Object.keys(stepsConfig);
      dispatch(
        action(ClientSideWizardActionTypes.Init, {
          data: isEmpty(state.formData.data) ? preloadedFormState.data : preloadedPristineFormConfig.data,
          stepId: stepsKeys[0],
          currentStep: !!preloadedFormState.currentStep ? preloadedFormState.currentStep : stepsKeys[0],
          totalSteps: stepsStatus.totalSteps,
          completedSteps: stepsStatus.nextCompletedSteps,
          preloadDataAdapter: wizardPreloadDataAdaptersMapping(preloadedFormState.preloadDataAdapterName),
          validationOptions: preloadedFormState.validationOptions,
        }),
      );
    } else {
      // Navigate to next/previous step
      const stepsKeys = Object.keys(stepsConfig);
      const foundIndex = stepsKeys.indexOf(state.currentProcessState.stepId!);
      const isFirstStep = foundIndex === 0;

      if (shouldGotoNextWizardStep) {
        dispatch(action(ClientSideWizardActionTypes.GoToNextStep, {}));
        onStepChange && onStepChange(stepsStatus.nextCompletedSteps, stepsStatus.totalSteps);
      }

      if (shouldGotoPreviousWizardStep && !isFirstStep) {
        dispatch(action(ClientSideWizardActionTypes.GoToPreviousStep, {}));
        onStepChange && onStepChange(state.currentProcessState.completedSteps - 1, stepsStatus.totalSteps);
      }
    }
    // reset navigation state
    setShouldNavigateTo(null);
  }, [
    stepsConfig,
    onStepChange,
    preloadedPristineFormConfig,
    state.formData,
    onPreviousFirstStep,
    stepsStatus.totalSteps,
    preloadedFormState.data,
    preloadedFormState.preloadDataAdapterName,
    preloadedFormState.validationOptions,
    shouldGotoNextWizardStep,
    isCurrentProcessStateExists,
    shouldGotoPreviousWizardStep,
    stepsStatus.nextCompletedSteps,
    preloadedFormState.currentStep,
    state.currentProcessState.stepId,
    state.currentProcessState.completedSteps,
  ]);

  // Update current step state
  useEffect(() => {
    if (!state.initialized || state.currentProcessState.stepId === currentProcessState.stepId) {
      return;
    }
    if (state.currentProcessState.stepId !== currentProcessState.stepId) {
      setCurrentProcessState(state.currentProcessState);
    }
    let nextCompletedSteps = state.currentProcessState.completedSteps || 0;
    const nextStateData = {
      formData: state.formData,
      stepData: state.formData.data[state.formData.currentStep],
      setState: state.setState,
      stepsConfig,
      currentStepId: state.currentProcessState.stepId,
      formDataPreloader,
      completedSteps: nextCompletedSteps,
    };
    stateHandler(nextStateData as ClientSideWizardStateHandlerProps, state, stateHandlerDispatch);
  }, [
    stepsConfig,
    stateHandler,
    state.formData,
    state.setState,
    state.initialized,
    formDataPreloader,
    stateHandlerDispatch,
    state.currentProcessState,
    currentProcessState.stepId,
    state.currentProcessState.stepId,
    state.currentProcessState.completedSteps,
    state,
  ]);

  return (
    <ClientSideWizardContext.Provider value={state}>
      <ClientSideWizardDispatchContext.Provider value={dispatch}>{children}</ClientSideWizardDispatchContext.Provider>
    </ClientSideWizardContext.Provider>
  );
};

export { ClientSideWizardProvider };
